// Reveal-only device-inventory cleanup (change ftue-reveal-flow-cleanup, ③).
//
// The reveal's Devices beat is a CONFIDENCE beat — "we found your stuff" — not a
// device manager. The raw `/v1/boats/:id/devices` list is one row per
// `vessel_devices` record with no dedup, so during discovery it shows:
//   - unresolved `:pending` placeholders whose displayName falls back to the raw
//     id string (core makeN2kId: `n2k:<src>:<addr>:<canName|'pending'>`), and
//   - the same physical device under more than one record (a transient
//     :pending twin before it re-keys; a Signal-K bridge view alongside the
//     native N2K view).
//
// This module cleans the list FOR PRESENTATION ONLY. It never touches what the
// boat reports or the cloud stores — the device-set the reinstall/`confirm-adopt`
// resolver quorums on is unchanged (spec: "de-duplication does not change the
// stored device-set"). Deeper identity unification (folding SK/N2K by unique
// number on the emit path) is a non-goal; this is a conservative render-time
// pass that removes the two reveal-specific offenders.
//
// Pure (no React/DOM/fetch) so it is unit-testable in isolation, like the rest of
// lib/revealStaging.ts.

import type { VesselDevice } from './useBoatDevices';

/**
 * A device whose identity has NOT resolved yet — still a bus placeholder. Its id
 * carries a `pending` marker (the canName segment is literally `pending` until
 * the address-claim PGN 60928 reveals the real CAN name), and its displayName
 * falls back to that raw id. Not a device to show in a confidence beat; it
 * reappears as its real self the moment it resolves.
 */
export function isUnresolvedPlaceholder(d: VesselDevice): boolean {
  // `pending` as a whole `:`-delimited segment of the wire id, or a trailing one.
  if (/(^|:)pending(:|$)/i.test(d.deviceId)) return true;
  if (d.canName != null && d.canName.trim().toLowerCase() === 'pending') return true;
  return false;
}

/**
 * A physical-identity key for collapsing two records of ONE device.
 *
 * The N2K **CAN NAME** is the correct fingerprint: it is the stable, globally
 * unique per-device identity (manufacturer + unique-number + instance), and it
 * is the SAME across a device's native-gateway view (`n2kphys:…`) and its Signal
 * K-bridge view (`sk:…`) — which is exactly the duplication we collapse. It also
 * stays DISTINCT for the separate sub-devices of one physical unit: a NAC-3
 * autopilot exposes 6 nodes (autopilot, rudder feedback, mode controller, …)
 * with 6 different canNames but ONE shared serial — so serial (and src_address,
 * which is unstable and absent on SK rows) must NOT be the key, or those real
 * devices would be wrongly merged away.
 *
 * A record with no canName (a Signal K-only device — e.g. Victron gear bridged
 * from VE.Direct) has no N2K NAME and no native twin, so it can't collide: keep
 * it by its own wire id rather than risk merging two distinct units.
 */
function physicalKey(d: VesselDevice): string {
  const can = d.canName?.trim();
  if (can != null && can !== '') return `can:${can}`;
  return `id:${d.deviceId}`;
}

const PROTO_RANK: Record<string, number> = { n2k: 0, n0183: 1, venus: 2, starlink: 2, sk: 3 };

/**
 * When two records collapse to one physical device, keep the richer one: a
 * record with a real human name beats one still showing its raw id; then prefer
 * the native protocol (n2k > n0183 > venus, starlink > sk); then the most recently seen.
 */
function preferDevice(a: VesselDevice, b: VesselDevice): VesselDevice {
  const aNamed = a.displayName !== a.deviceId;
  const bNamed = b.displayName !== b.deviceId;
  if (aNamed !== bNamed) return aNamed ? a : b;
  const ar = PROTO_RANK[a.protocol] ?? 9;
  const br = PROTO_RANK[b.protocol] ?? 9;
  if (ar !== br) return ar < br ? a : b;
  return b.lastSeen > a.lastSeen ? b : a;
}

/**
 * The reveal's clean inventory: drop unresolved placeholders, collapse to one
 * entry per physical device, preserve first-appearance order (the API already
 * sorts by manufacturer/model/id, so this is stable and sensible). Pure.
 */
export function cleanRevealDevices(devices: readonly VesselDevice[]): VesselDevice[] {
  const byKey = new Map<string, VesselDevice>();
  const order: string[] = [];
  for (const d of devices) {
    if (isUnresolvedPlaceholder(d)) continue;
    const key = physicalKey(d);
    const existing = byKey.get(key);
    if (existing === undefined) {
      byKey.set(key, d);
      order.push(key);
    } else {
      byKey.set(key, preferDevice(existing, d));
    }
  }
  return order.map((k) => byKey.get(k)!);
}
